import create from "zustand";
import _ from "lodash";
import { useOrgChartStore } from "../../../../../../../stores/use-org-chart-store";

const deepFind =
  (callbackFn) =>
  ([firstInArray, ...restOfArray] = []) => {
    return (
      firstInArray &&
      (callbackFn(firstInArray)
        ? firstInArray
        : deepFind(callbackFn)(firstInArray.children) ||
          deepFind(callbackFn)(restOfArray))
    );
  };

const findParentOfUser = (userId) => (structure) =>
  deepFind((user) => user.children.find((node) => node.id === userId))([
    structure,
  ]);

const removeFromTree = (node, id) => {
  if (node.id === id) {
    node = undefined;
  } else {
    node.children.forEach((child, index) => {
      if (!removeFromTree(child, id)) {
        node.children.splice(index, 1);
      }
    });
  }

  return node;
};

const deepFindAll = (list, predicate) => {
  const found = [];

  for (const node of list) {
    if (predicate(node)) {
      found.push(node);
    }
    if (node.children) {
      const result = deepFindAll(node.children, predicate);
      if (result?.length) found.push(...result);
    }
  }

  return found;
};

const findUserById = (id) => (structure) =>
  deepFind((user) => user.id === id)([structure]);

const buildLevelsFromStructure = (
  structure,
  levelsWithUsers = [],
  iteration = 1
) => {
  levelsWithUsers.push({ level: iteration, users: [...structure] });

  const allChildrenFromUsers = [];
  for (let index = 0; index < structure.length; index++) {
    if (structure[index]?.children?.length) {
      allChildrenFromUsers.push(...structure[index]?.children);
    }
  }

  if (allChildrenFromUsers?.length) {
    buildLevelsFromStructure(
      allChildrenFromUsers,
      levelsWithUsers,
      iteration + 1
    );
  }

  return levelsWithUsers;
};

const checkIsDescendantOfUser = (potentialParent, user) => {
  const usersOfParent = deepFindAll(
    [potentialParent],
    (node) => node?.id === user?.id
  );
  return !!usersOfParent?.length;
};

export const useStructureStore = create((set, get) => ({
  structure: {
    id: "root-level",
    expanded: true,
    children: [],
  },
  setStructure: (data) => set((state) => ({ structure: data })),

  selectedUser: undefined,
  setSelectedUser: (selectedUser) => set((state) => ({ selectedUser })),

  levelsWithUsers: [],
  setLevelsWithUsers: (levels) => set((state) => ({ levelsWithUsers: levels })),

  selectedLevel: undefined,
  setSelectedLevel: (level) => set((state) => ({ selectedLevel: level })),

  usersWithoutSupervisor: [],
  setUsersWithoutSupervisor: (users) =>
    set((state) => ({ usersWithoutSupervisor: users })),

  numberOfUsersInDynamicChart: undefined,
  setNumberOfUsersInDynamicChart: (numberOfUsers) =>
    set((state) => ({ numberOfUsersInDynamicChart: numberOfUsers })),

  checkIsUserInStructure: (userAccountId) => {
    const structureCopy = { ...get().structure };
    const found = findUserById(userAccountId)(structureCopy);
    return !!found;
  },

  addUsersToStructure: (userIds, parentId) => {
    const contentBuilderState = useOrgChartStore.getState();
    const structureCopy = { ...get().structure };

    const updateStructureForSingleUser = (userId) => {
      if (!parentId) {
        structureCopy.children.push({
          id: userId,
          expanded: true,
          children: [],
        });
      } else {
        let found = findUserById(parentId)(structureCopy);
        if (!found) {
          structureCopy.children.push({
            id: userId,
            expanded: true,
            children: [],
          });
        } else {
          found.children.push({ id: userId, expanded: true, children: [] });
        }
      }
    };

    if (!Array.isArray(userIds) && typeof userIds === "string") {
      updateStructureForSingleUser(userIds);
    } else {
      userIds.forEach((userId) => {
        updateStructureForSingleUser(userId);
      });
    }

    contentBuilderState.setData({
      structure: structureCopy,
    });
    return set((state) => ({ structure: structureCopy }));
  },

  getLevelsWithUsers: () => {
    const structureCopy = { ...get().structure };
    const levelsWithUsers = buildLevelsFromStructure(structureCopy.children);
    return levelsWithUsers;
  },

  removeUser: (userId) => {
    const contentBuilderState = useOrgChartStore.getState();
    const structureCopy = { ...get().structure };

    const foundUser = findUserById(userId)(structureCopy);
    if (!foundUser) {
      return;
    }

    const currentUserParentIsRoot = structureCopy.children.some(
      (childUser) => childUser.id === userId
    );
    const parentOfUser = !currentUserParentIsRoot
      ? findParentOfUser(structureCopy, userId)
      : structureCopy;
    const childrenOfFoundUser = foundUser.children;

    if (childrenOfFoundUser && parentOfUser?.children) {
      parentOfUser.children = [
        ...parentOfUser.children,
        ...childrenOfFoundUser,
      ];
    }

    removeFromTree(structureCopy, userId);

    contentBuilderState.setData({
      structure: structureCopy,
    });
    return set((state) => ({
      selectedUser: undefined,
      structure: structureCopy,
    }));
  },

  reorder: (source, target) => {
    if (target.accountId === source.accountId) return;

    const contentBuilderState = useOrgChartStore.getState();
    const structureCopy = { ...get().structure };

    const draggedUser = findUserById(source.accountId)(structureCopy);
    const targetUser = findUserById(target.accountId)(structureCopy);

    if (!draggedUser || !targetUser) return;

    if (target.type === "same-level-user") {
      const isTargetUsersParentRoot = structureCopy.children.some(
        (childUser) => childUser.id === targetUser.id
      );
      const parentOfTargetUser = isTargetUsersParentRoot
        ? structureCopy
        : findParentOfUser(targetUser.id)(structureCopy);

      const indexOfTargetUser = parentOfTargetUser.children.findIndex(
        (node) => node.id === targetUser.id
      );
      removeFromTree(structureCopy, draggedUser.id);
      parentOfTargetUser.children.splice(
        indexOfTargetUser === 0 ? 1 : indexOfTargetUser,
        0,
        { ...draggedUser }
      );

      contentBuilderState.setData({
        structure: structureCopy,
      });
      return set((state) => ({ structure: { ...structureCopy } }));
    }

    const isDraggedItemParentOfTarget = checkIsDescendantOfUser(
      draggedUser,
      targetUser
    );

    if (isDraggedItemParentOfTarget) {
      const isDraggedUsersParentRoot = structureCopy.children.some(
        (childUser) => childUser.id === draggedUser.id
      );

      const parentOfDraggedUser = isDraggedUsersParentRoot
        ? structureCopy
        : findParentOfUser(draggedUser.id)(structureCopy);

      targetUser.children.push({
        ...removeFromTree(draggedUser, targetUser.id),
      });
      draggedUser.id = `${draggedUser.id}-temp`;
      parentOfDraggedUser?.children?.push({ ...targetUser });

      removeFromTree(structureCopy, draggedUser.id);
    } else {
      removeFromTree(structureCopy, source.accountId);
      targetUser.children.push({ ...draggedUser });
    }

    contentBuilderState.setData({
      structure: structureCopy,
    });
    return set((state) => ({ structure: { ...structureCopy } }));
  },

  checkIfUserIsDescendantOfOtherUser: (userId, potentialParentId) => {
    const structureCopy = { ...get().structure };

    const userToCheck = findUserById(userId)(structureCopy);
    const parentUser = findUserById(potentialParentId)(structureCopy);

    if (!userToCheck || !parentUser) return false;

    return checkIsDescendantOfUser(parentUser, userToCheck);
  },

  updateNodeExpandedState: (node) => {
    const contentBuilderState = useOrgChartStore.getState();
    const structureCopy = { ...get().structure };

    let foundUser = findUserById(node.id)(structureCopy);

    if (!foundUser) return;

    foundUser.expanded = !node.expanded;

    contentBuilderState.setData({
      structure: structureCopy,
    });
    return set((state) => ({ structure: structureCopy }));
  },

  getUsersFromSpecificLevel: (specifiedLevel, customStructure) => {
    if (specifiedLevel === 0) return [];

    const structureCopy = customStructure
      ? { ...customStructure }
      : { ...get().structure };
    const users = deepFindAll(
      [{ ...structureCopy }],
      (node) => node.level === specifiedLevel
    );
    return users;
  },
}));
